import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  getInlineEditColumnType,
  InlineEditDataType,
  ModalLinkRendererParams,
  NgGridComponents,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { omit } from 'lodash-es'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import {
  BulkDirectTransferRow,
  directTransferField,
  OrigUnitsRequest,
  PriceInputInfoTransferCreate,
} from '../../../model/bulk-transaction/bulk-direct-transfer/bulk-direct-transfer.model'
import {
  BulkTransactionModalInput,
  BulkTransferPositionData,
} from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkDirectTransferTransaction,
  BulkMvtResponse,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { Position } from '../../../model/bulk-transaction/bulk-transfer-out-all-positions-model'
import { BulkDirectTransferValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { inventories, InventoryType } from '../../../model/inventory-type.model'
import {
  Basis,
  MvtBasis,
  MvtBasisSelection,
} from '../../../model/mvt-basis.model'
import { PricingRequest, PricingResponse } from '../../../model/pricing.model'
import { SecurityMasterData } from '../../../model/security.model'
import { MarketValueTransferModel } from '../../../model/single-transaction/market-value-transfer.model'
import { Transaction } from '../../../model/single-transaction/transaction'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  RequestMode,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  isBulkCopyModal,
  isBulkEditModal,
} from '../../../util/bulk-popup-input-utils'
import {
  buildMvtBasisSelection,
  BULK_DIALOG_EVENT_NAME,
  bulkMetricsDetail,
  bulkRequestMode,
  convertBulkResponseKeyToPricingRequest,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  numberValueFormatter,
  onSortChange,
  showOrderIDColumn,
  showTranIdColumn,
} from '../../../util/bulk-specific-common-utils'
import { mapDirectTransferToBulkTransaction } from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getTradeTypes,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { BvMvBasisComponent } from '../../bv-mv-basis-selector/bv-mv-basis-selector.component'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'

@Component({
  selector: 'hs-bulk-direct-transfer',
  templateUrl: './bulk-direct-transfer.component.html',
  styleUrls: ['./bulk-direct-transfer.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkDirectTransferModalComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput
  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)
  private readonly adjustTransactionsPostDeleteBinding =
    this.adjustTransactionsPostDelete.bind(this)

  title = 'Direct Transfer Bulk Create'
  getFieldValidatorFormatter = getFieldValidatorFormatter
  formSubmitting = false
  allDataValidated = false
  transactionLoaded = false
  deletePostProcessingInProgress = false
  rowData: BulkDirectTransferRow[] = []
  transTypes = getTradeTypes().map(t => t.name)
  colDefs: ColDef[] = this.getColDefs()
  gridOptions: GridOptions = this.createGridOptions()
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  userId?: number
  pricingInfos: Map<string, PricingResponse> = new Map<
    string,
    PricingResponse
  >()
  mvtDataLoaded: Map<string, Promise<MvtBasis>> = new Map<
    string,
    Promise<MvtBasis>
  >()
  note = new FormControl('')
  loadingBasis = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  isDataValid = false
  isCommonValidationError = false
  showConfirmButtonModal = false
  disableSaveValidRows = false
  isValidating = false
  disableValidate = false
  errorFields: string[] = []
  gridApi?: AgGridApi
  executionId?: number
  externalAccountId?: number
  originalCusip?: string
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  private readonly destroySubject$ = new Subject<void>()
  genericWarnings: string[] = []
  hasTransactionMatchCustody = false
  hasTransactionMatchTrading = false
  currentCounterParty?: CounterParty
  currentRowIndex = 0
  loadingDialog = false
  sortable = false
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  disableClear = false
  isEdit = false
  originalTransactions: BulkDirectTransferTransaction[] = []
  mvtMapByTransaction: {
    [key: string]: { [key: string]: MvtBasis }
  } = {}
  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}
  activeInstances: NgbModalRef[] = []
  compId = 'bulk-direct-transfer-' + Math.random()

  constructor(
    public toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    readonly modalService: NgbModal,
    readonly store: Store<RootState>,
    readonly bulkTransactionService: CreateEditTransactionService,
    readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })
    if (this.transInput) {
      showTranIdColumn(this.gridOptions, this.transInput, this.sortable)
      if (
        isBulkEditModal(this.transInput) &&
        this.transInput.transactions?.[0].orderId
      ) {
        showOrderIDColumn(this.gridOptions, this.sortable)
      }
    }
    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      this.errorDetails = error.error
    }
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)
    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )

    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Direct-Transfer-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.loadingDialog = true
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.document.addEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )

    if (this.transInput?.cwTranId) {
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId ?? [],
      )
      this.transInput.transactions = mapDirectTransferToBulkTransaction(
        this.clearwaterTransactionDetail,
        this.securityMasterData,
      )
    }

    this.loadDataCreate()
    if (!isBulkCopyModal(this.transInput) && this.transInput?.transactions) {
      this.title = 'Direct Transfer Bulk Edit'
      this.disableClear = true
      this.isEdit = true
      this.pasteService.setIsEdit()
      if (isBulkEditModal(this.transInput)) {
        this.originalTransactions = this.transInput
          ?.transactions as BulkDirectTransferTransaction[]

        const transactions = this.transInput
          ?.transactions as BulkDirectTransferTransaction[]
        await this.loadMvtDetailsBulk(transactions)
        transactions.forEach((trans, index) => {
          this.rowData.push(
            this.generateDataEntryFromDirectTransfer(trans, index),
          )
        })
      }
    } else if (this.transInput?.positionData) {
      await this.processDataForTransferAllPositions(
        this.transInput?.positionData,
      )
    } else if (this.transInput?.transferAllPositionsRequest) {
      let transferAllPositionData: Position[] = []
      const positionData: BulkTransferPositionData[] = []
      const uniquePositions: Set<string> = new Set<string>()
      await this.bulkTransactionService
        .loadAllPositionsForTransferOut(
          this.transInput?.transferAllPositionsRequest,
        )
        .toPromise()
        .then(response => {
          transferAllPositionData = response
        })
      transferAllPositionData.forEach(data => {
        const key = `${data.secId}-${data.acctId}-${data.inventoryType}-${data.collateralBroker}`
        if (!uniquePositions.has(key)) {
          uniquePositions.add(key)
          positionData.push(
            this.buildTransferAllPositionData(
              data,
            ) as unknown as BulkTransferPositionData,
          )
        }
      })
      await this.processDataForTransferAllPositions(positionData)
    } else if (isBulkCopyModal(this.transInput)) {
      this.originalTransactions = this.transInput
        ?.transactions as BulkDirectTransferTransaction[]

      const transactions = this.transInput
        ?.transactions as BulkDirectTransferTransaction[]
      const pricingRequest = new Set<string>()
      const rowDataValue: BulkDirectTransferRow[] = []
      transactions.forEach((data, index) => {
        const { collateralBroker, inventoryType } = data

        if (data.accountId && data.entryDate && data.cusip) {
          const currentPriceInputInfoTransfer: PriceInputInfoTransferCreate = {
            accountId: Number(data.accountId),
            cusip: data.cusip,
            entryDate: data.entryDate,
            ...(inventoryType && { inventoryType }),
            ...(collateralBroker && { collateralBroker }),
          }
          pricingRequest.add(JSON.stringify(currentPriceInputInfoTransfer))
        }

        const transferData = this.generateDataEntryFromDirectTransfer(
          data,
          index,
        )
        rowDataValue.push(transferData)
      })
      await this.loadPricingInfoUsingAssetIds(pricingRequest)
      this.rowData = rowDataValue
      this.rowData.push(
        this.generateEmptyDataEntry(false, this.rowData.length + 1),
      )
    } else {
      this.createFreshRowData()
    }

    this.gridApi?.api.applyTransaction({ add: this.rowData })
    if (isBulkCopyModal(this.transInput)) {
      this.gridApi?.api.forEachNode(rowNode => {
        if (this.shouldOpenMvtPicker(rowNode.data)) {
          if (this.shouldOpenMvtPicker(rowNode.data)) {
            this.initializeMvtBasis(rowNode, true)
          } else {
            rowNode.setDataValue(
              'mvtBasisSelectionText',
              TransactionConstants.MIN_FIELD_MESSAGE,
            )
            rowNode.setDataValue('mvtBasisSelection', [])
          }
        }
      })
    }

    this.loadingDialog = false
  }

  buildTransferAllPositionData(data: Position) {
    return {
      sessionReconDate: this.sessionReconDate,
      assetId: data.secIdentifier,
      accountId: data.acctId,
      units: data.cwanOriginalUnits ?? '',
      securityId: data.secId,
      collateralBroker: data.collateralBroker,
      inventoryType: data.inventoryType ?? 'LONG',
    }
  }

  async processDataForTransferAllPositions(
    positionData: BulkTransferPositionData[],
  ) {
    const pricingRequest = new Set<string>()
    const positionRowData: BulkDirectTransferRow[] = []
    positionData.forEach((data, index) => {
      const { collateralBroker, inventoryType } = data
      const currentPriceInputInfoTransfer: PriceInputInfoTransferCreate = {
        accountId: Number(data.accountId),
        cusip: data.assetId,
        entryDate: data.sessionReconDate,
        ...(inventoryType && { inventoryType }),
        ...(collateralBroker && { collateralBroker }),
      }
      pricingRequest.add(JSON.stringify(currentPriceInputInfoTransfer))
      const positionTransferData = this.generateDataEntryFromPositionTransfer(
        data,
        index,
      )
      positionRowData.push(positionTransferData)
    })
    await this.loadPricingInfoUsingAssetIds(pricingRequest)
    this.rowData = positionRowData
    this.setPricingInfoInRowData()
  }

  setPricingInfoInRowData() {
    const priceInfo: Map<string, number | undefined> = new Map()

    for (const [key, value] of this.pricingInfos) {
      // example - we get key as '{"accountId":4,"cusip":"126650BJ8","entryDate":"2024-04-01"}'
      // extracting value of cusip from the above string using regEx
      const cusipMatch = /"cusip":"([^"]*)"/.exec(key)
      const cusip = cusipMatch ? cusipMatch[1] : ''
      priceInfo.set(cusip, value.price)
    }

    //Setting the marketPrice in the rowData using priceInfo
    this.rowData.forEach(row => {
      const matchingPrice = priceInfo.get(row.assetId)
      if (matchingPrice) row.marketPrice = matchingPrice.toString()
    })
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
    this.document.removeEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
  }

  async loadTransactionDetailBulk(
    cwTransId: string[],
    securityId: number[] | undefined,
  ) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.TRANSFER,
        true,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        if (this.transInput) {
          const unorderedTransactionDetails =
            transactionDetail.transactionDataMap
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const orderedTransactionDetails: { [key: string]: any } = {}

          // Sort the transaction details based on the order of cwTransid
          for (const id of cwTransId) {
            if (unorderedTransactionDetails[id]) {
              orderedTransactionDetails[id] = unorderedTransactionDetails[id]
            }
          }

          this.clearwaterTransactionDetail = orderedTransactionDetails
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(transactionDetail.securityMasterDataMap ?? ''),
          )
        }
      },
    )
  }

  /** @internal */
  loadDataCreate() {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
  }

  /** @internal */
  async loadPricingInfoUsingAssetIds(pricingRequestStrings: Set<string>) {
    this.loadingText = TransactionConstants.LOAD_PRICING
    this.errorDetails = ''
    if (pricingRequestStrings.size == 0) {
      await new Promise(f => setTimeout(f, 40))
      return
    }
    const pricingRequests: PricingRequest[] = []

    pricingRequestStrings.forEach(pricingRequestString => {
      const pricingRequest: PricingRequest = JSON.parse(pricingRequestString)
      pricingRequests.push(pricingRequest)
    })
    const pricingPromise =
      this.bulkTransactionService.loadBulkPricingInformation(pricingRequests)

    return pricingPromise
      .toPromise()
      .then(pricingInfo => {
        const pricingData = new Map<string, PricingResponse>(
          Object.entries(pricingInfo),
        )
        pricingData.forEach((value, key) => {
          const responseKeyToPricingRequest =
            convertBulkResponseKeyToPricingRequest(key)
          this.pricingInfos.set(
            JSON.stringify(responseKeyToPricingRequest),
            value,
          )
        })
      })
      .catch(error => {
        this.errorHeader = TransactionConstants.ERROR_PRICING
        this.errorDetails = error.error
        this.loadingDialog = false
      })
  }

  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.pasteService.registerGrid(event.api, event.columnApi)
    this.copyService.registerGrid(event.api)
    this.bulkDeleteService.registerGrid(event.api)
    await this.savedViewService.manageSavedView(
      event,
      'Bulk-direct-transfer-workspace-key',
      this.destroySubject$,
    )
    this.gridApi.api.setSortModel(null)
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => this.onGridReady(event),
      getRowNodeId: ({ rowId }: BulkDirectTransferRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },

      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
    }
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: directTransferField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: directTransferField('rowNumber'),
        sortable: false,
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
      },
      {
        field: directTransferField('errorMessageMap'),
        hide: true,
      },
      {
        field: directTransferField('entryDate'),
        headerName: 'Entry Date',
        width: 100,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: directTransferField('assetId'),
        headerName: 'Asset ID',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },
      {
        field: directTransferField('sourceAccountId'),
        headerName: 'Source Account ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 120,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },
      {
        field: directTransferField('destAccountId'),
        headerName: 'Dest Account ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 120,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },

      {
        field: directTransferField('units'),
        headerName: 'Units',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: directTransferField('inventoryType'),
        headerName: 'Inventory Type',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: inventories,
            bindValue: 'name',
            bindLabel: 'name',
          },
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: directTransferField('marketPrice'),
        headerName: 'Market Price',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: directTransferField('collateralBroker'),
        headerName: 'Collateral Broker',
        width: 115,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,

        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: directTransferField('transferSpecificLotId'),
        headerName: 'Transfer Specific Lot ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 115,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },

      {
        field: directTransferField('mvtBasisSelectionText'),
        editable: true,
        sortable: true,
        headerName: 'Market Value Transfer',
        width: 225,
        tooltipValueGetter: params => {
          return this.shouldOpenMvtPicker(params?.data)
            ? ''
            : 'Please configure Entry date, Source account ID and Destination Account ID before adjusting basis'
        },
        tooltipComponentParams: {
          showTooltip: params => !this.shouldOpenMvtPicker(params?.data),
        } as NfTooltipParams,
        cellRenderer: undefined,
        cellRendererSelector: (params: ICellRendererParams) => {
          return this.getPopUpRenderer(params)
        },
      },
      {
        field: directTransferField('mvtBasisSelection'),
        hide: true,
      },
      {
        field: directTransferField('balanceUnits'),
        hide: true,
      },
      {
        field: directTransferField('transactionBasis'),
        hide: true,
      },
    ]
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  adjustTransactionsPostDelete() {
    this.deletePostProcessingInProgress = true
    const newRowData: BulkDirectTransferRow[] = []
    this.gridApi?.api.forEachNode((rowNode, index) => {
      rowNode.data.rowId = (index + 1).toString()
      newRowData.push(rowNode.data)
    })
    this.gridApi?.api.setRowData(newRowData)
    this.deletePostProcessingInProgress = false
  }

  async onPasteEnd(evt: Event) {
    const pasteDetails = (evt as CustomEvent).detail
    this.disableValidate = true
    const completePastedRowData: BulkDirectTransferRow[] =
      pasteDetails.pastedRowData
    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkDirectTransferRow }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet

    const pricingRequest = new Set<string>()
    completePastedRowData
      .filter(rowData => this.hasNecessaryInfoForPriceLoading(rowData))
      .forEach(rowDataValid => {
        const { collateralBroker, inventoryType } = rowDataValid
        const currentPriceInputInfoTransfer: PriceInputInfoTransferCreate = {
          accountId: Number(rowDataValid.sourceAccountId),
          cusip: rowDataValid.assetId,
          entryDate: rowDataValid.entryDate,
          ...(inventoryType && { inventoryType }),
          ...(collateralBroker && { collateralBroker }),
        }
        if (
          !this.getPricingInfoByPricingRequest(
            JSON.stringify(currentPriceInputInfoTransfer),
          )
        ) {
          pricingRequest.add(JSON.stringify(currentPriceInputInfoTransfer))
        }
      })

    this.loadingDialog = true
    await this.loadPricingInfoUsingAssetIds(pricingRequest)

    await new Promise(f => setTimeout(f, 40))
    this.applyOnChangeForPastedRowNodes()

    this.loadingDialog = false
    this.disableValidate = false
  }

  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        if (displayedNode.data.mvtBasisSelection === '') {
          displayedNode.data.mvtBasisSelection = []
        }
        if (this.shouldOpenMvtPicker(displayedNode.data)) {
          this.initializeMvtBasis(
            displayedNode,
            !this.lastColumnIdsPasted.has('mvtBasisSelectionText'),
          )
        } else {
          displayedNode.setDataValue(
            'mvtBasisSelectionText',
            TransactionConstants.MIN_FIELD_MESSAGE,
          )
          displayedNode.setDataValue('mvtBasisSelection', [])
        }
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged(
          {
            column: { getColId: () => colId },
            node: displayedNode,
            data: displayedNode.data,
            newValue: displayedNode?.data[colId ?? ''],
            oldValue: '',
          } as unknown as CellValueChangedEvent,
          this.lastColumnIdsPasted,
        )
      })
  }

  hasNecessaryInfoForPriceLoading(data: BulkDirectTransferRow): unknown {
    return (
      data.sourceAccountId != '' && data.entryDate != '' && data.assetId != ''
    )
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0)
    ) {
      return true
    }
    return false
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)

    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return impactedFieldColDef?.field == 'counterParty' ? params.value : ''
    }
  }

  deduceImpactedField(impactedFieldFromValidationInfo: string): string {
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'assetId'
    } else if (impactedFieldFromValidationInfo == 'accountId') {
      return 'sourceAccountId'
    } else if (impactedFieldFromValidationInfo == 'transferPrice') {
      return 'marketPrice'
    } else if (impactedFieldFromValidationInfo == 'specLotMatchDetails') {
      return 'transferSpecificLotId'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }

  onColumnValueChanged(
    event: CellValueChangedEvent,
    columnPastedIds?: Set<string>,
  ) {
    if (
      event.newValue == event.oldValue ||
      this.deletePostProcessingInProgress
    ) {
      return
    }
    const rowNode = event.node
    const colId = event.column.getColId()

    if (
      rowNode?.data.validityStatus == 'inactive' &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (rowNode.data.inventoryType == '') {
        rowNode.data.inventoryType = 'LONG'
      }

      if (
        Number(rowNode?.rowIndex) + 1 ==
        this.gridApi?.api.getDisplayedRowCount()
      ) {
        const rows = [
          this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
        ]
        this.gridApi?.api.applyTransaction({ add: rows })
      }
    }
    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }
    switch (colId) {
      case 'assetId':
      case 'entryDate':
      case 'sourceAccountId':
        if (colId != 'assetId' && !columnPastedIds) {
          if (this.shouldOpenMvtPicker(rowNode.data)) {
            this.initializeMvtBasis(rowNode, true)
          } else {
            rowNode.setDataValue(
              'mvtBasisSelectionText',
              TransactionConstants.MIN_FIELD_MESSAGE,
            )
            rowNode.setDataValue('mvtBasisSelection', [])
          }
        }

        if (!columnPastedIds?.has('marketPrice'))
          this.loadPricingOnAssetIdChange(event.data, event.node)
        break
      case 'destAccountId':
        if (!columnPastedIds) {
          if (this.shouldOpenMvtPicker(rowNode.data)) {
            this.initializeMvtBasis(rowNode, true)
          } else {
            rowNode.setDataValue(
              'mvtBasisSelectionText',
              TransactionConstants.MIN_FIELD_MESSAGE,
            )
            rowNode.setDataValue('mvtBasisSelection', [])
          }
        }
        break
      case 'units':
        if (event.node.data.inventoryType != 'ISSUE')
          event.node.setDataValue('units', Math.abs(Number(event.newValue)))
        break
      case 'inventoryType':
        if (event.value != 'ISSUE') {
          event.node.setDataValue(
            'units',
            Math.abs(Number(event.node.data.units)),
          )
        }
        break
      case 'mvtBasisSelectionText':
        this.handleMvtSelectedChanged(event.node)
        break
    }
  }

  loadPricingOnAssetIdChange(
    data: BulkDirectTransferRow,
    currentRowNode: RowNode,
  ) {
    const cusip = data.assetId
    const entryDate = data.entryDate
    const accountId = Number(data.sourceAccountId)
    const inventoryType = data.inventoryType
    const collateralBroker = data.collateralBroker
    const currentPriceInputInfoTransfer: PriceInputInfoTransferCreate = {
      accountId,
      cusip,
      entryDate,
      ...(inventoryType && { inventoryType }),
      ...(collateralBroker && { collateralBroker }),
    }
    const pricingRequestForRequest = {
      accountId: currentPriceInputInfoTransfer.accountId,
      cusip: currentPriceInputInfoTransfer.cusip,
      entryDate: currentPriceInputInfoTransfer.entryDate,
    }
    if (cusip && entryDate && accountId) {
      const pricingExisting = this.getPricingInfoByPricingRequest(
        JSON.stringify(pricingRequestForRequest),
      )

      if (pricingExisting) {
        this.updatePricingInfo(
          pricingRequestForRequest,
          pricingExisting,
          currentRowNode,
        )
      } else {
        this.bulkTransactionService
          .loadPricingInformation(currentPriceInputInfoTransfer)
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(pricingInfo =>
            this.updatePricingInfo(
              pricingRequestForRequest,
              pricingInfo,
              currentRowNode,
            ),
          )
      }
    }
  }

  updatePricingInfo(
    pricingRequest: PricingRequest,
    pricingInfo: PricingResponse | null,
    currentRowNode: RowNode,
  ) {
    if (pricingInfo == null) {
      return
    }
    currentRowNode.setDataValue('marketPrice', pricingInfo.price ?? '')
    this.pricingInfos.set(JSON.stringify(pricingRequest), pricingInfo)
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    // Detect on the grid's own account field, not the literal 'accountId': this grid stores
    // the account under 'sourceAccountId', so detecting on 'accountId' always reads undefined
    // and flags every row as missing even after the user fills it in.
    const accountField = this.deduceImpactedField('accountId')
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      accountField,
      accountField,
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async deriveAndValidate() {
    if (this.markMissingAccountRows()) {
      return
    }
    await this.populateTransferDetails()
    await this.validate()
  }

  async populateTransferDetails() {
    const origUnitsPayload: OrigUnitsRequest[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      const { collateralBroker, inventoryType, transferSpecificLotId } =
        rowNode.data
      if (
        rowNode.data.validityStatus != 'inactive' &&
        rowNode.data.assetId != '' &&
        rowNode.data.entryDate != '' &&
        rowNode.data.accountId != ''
      ) {
        origUnitsPayload.push({
          cusip: rowNode.data.assetId,
          entryDate: rowNode.data.entryDate,
          accountId: Number(rowNode.data.sourceAccountId),
          rowId: Number(rowNode.data.rowId),
          ...(inventoryType && { inventoryType }),
          ...(collateralBroker && { collateralBroker }),
          ...(transferSpecificLotId && {
            lotEntryTranId: Number(transferSpecificLotId),
          }),
        })
      }
    })
    this.loadingText = 'Loading Units'
    this.isValidating = true
    try {
      const res = await this.bulkTransactionService
        .getTransferDetails(origUnitsPayload)
        .toPromise()
      origUnitsPayload.forEach(rowNode => {
        const balanceUnits = res.tranDetails[rowNode.rowId]?.balanceUnits
        if (balanceUnits !== undefined) {
          this.gridApi?.api.forEachNode(node => {
            if (Number(node.data.rowId) === Number(rowNode.rowId)) {
              node.setDataValue('balanceUnits', balanceUnits)
              if (!node.data.units) node.setDataValue('units', balanceUnits)
            }
          })
        }
      })
      this.isValidating = false
    } catch (error) {
      this.errorHeader = 'Error Fetching Units'
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  async validate() {
    this.gridApi?.api?.clearRangeSelection()
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        this.setMarketValueTextFromMvtSelected(
          rowNode.data.mvtBasisSelection,
          rowNode,
        )
      }
    })
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    this.errorDetails = ''
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransactionDirectTransfer(bulkValidateView)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isCommonValidationError = true
          this.isDataValid = false
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()
      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        const isError = validationInfo.level === ErrorSeverity.ERROR
        const messageMap = isError
          ? this.cellLevelErrorMessageMap
          : this.cellLevelWarningMessageMap
        const messages = isError ? errorMessages : warningMessages
        validationInfo.impactedFields.forEach((impactedField: string) => {
          const impactedFieldProcessed = this.deduceImpactedField(impactedField)
          const currentKey = `${index}-${impactedFieldProcessed}` // More efficient key representation
          // message map
          const msgList = messageMap.get(currentKey) || []
          msgList.push(validationInfo.message)
          messageMap.set(currentKey, msgList)
          // messages
          const fieldMessages = messages.get(impactedFieldProcessed) || []
          fieldMessages.push(validationInfo.message)
          messages.set(impactedFieldProcessed, fieldMessages)
        })
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  createBulkValidationView(): BulkDirectTransferValidationView {
    const view: BulkDirectTransferValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: RequestMode.NEW_TRAN,
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.TRANSFER,
    }
    if (this.note.value != '') view.transactionNote = this.note.value

    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }

  prepareBulkTransactionData(
    rowData: BulkDirectTransferRow,
    isValidate: boolean,
  ): TransactionRequestWrapper {
    const currentPricingInfo = this.getPricingInfoByPricingRequest(
      JSON.stringify({
        accountId: Number(rowData.sourceAccountId),
        cusip: rowData.assetId,
        entryDate: rowData.entryDate,
      }),
    )
    let currentOriginalTransaction = undefined
    if (!isBulkCopyModal(this.transInput)) {
      currentOriginalTransaction = this.originalTransactions.find(
        origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
      )
    }
    const cp = getDefaultUnknownCounterParty()
    const units = this.updateUnit(Number(rowData.units), rowData)
    const transactionData: BulkDirectTransferTransaction = {
      units: units,
      tranType: 'TRANSFER_OUT',
      createdDate: new Date().toISOString(),
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: Number(rowData.sourceAccountId),
      entryDate: rowData.entryDate,
      settleDate: rowData.entryDate,
      postDate: rowData.entryDate,
      modifiedDate: new Date().toISOString(),
      transferPurchaseInfoMap: {},
      active: true,
    }
    if (rowData.collateralBroker) {
      transactionData.dataMap = { collateralBroker: rowData.collateralBroker }
    } else {
      transactionData.dataMap = {}
    }
    if (!isValidate) {
      if (this.isEdit) {
        transactionData.securityId = rowData.securityId
      } else {
        transactionData.securityId = currentPricingInfo?.securityId ?? -1
      }

      transactionData.counterParty = cp
      transactionData.counterPartyId = cp.id
    }
    if (rowData.inventoryType != '') {
      transactionData.inventoryType = rowData.inventoryType as InventoryType
    }
    if (rowData.marketPrice !== '') {
      transactionData.transferPrice = Number(rowData.marketPrice)
    }

    if (
      (this.sessionReconDate &&
        !isAfter(
          new Date(rowData.entryDate),
          new Date(this.sessionReconDate),
        )) ||
      (!this.sessionReconDate &&
        this.selectedReconDate &&
        !isAfter(new Date(rowData.entryDate), new Date(this.selectedReconDate)))
    ) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
      transactionData.clearedDate = currentOriginalTransaction?.clearedDate
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      cusip: rowData.assetId,
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      destAccountId: Number(rowData.destAccountId),
      unitsRatio: 1.0,
      balanceUnits: Number(rowData.balanceUnits),
      costBasisProportion: 1.0,
      inBases: rowData.mvtBasisSelection
        .filter(bases => bases.marketValue)
        .map(baseSelected => omit(baseSelected.basis, 'checked')) as Basis[],
      basis: rowData.transactionBasis,
    }
    if (this.isEdit) {
      metaData.destSecurityId = rowData.securityId
      metaData.existingUnits =
        this.originalTransactions.find(
          origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
        )?.units ?? 0
    } else {
      if (rowData.transferSpecificLotId) {
        metaData.specLotMatchDetails = {
          lotEntryTranId: Number(rowData.transferSpecificLotId),
          units: units,
        }
      }
      metaData.destSecurityId = currentPricingInfo?.securityId ?? -1
    }
    const mvtBasis = this.mvtMapByTransaction[
      rowData.legacyOrigTranId ?? 0
    ] as unknown as MvtBasis
    if (mvtBasis?.mvtBases) {
      metaData.mvtBases = mvtBasis.mvtBases
    }

    return { transactionData, metaData }
  }

  updateUnit(unit: number, rowData: BulkDirectTransferRow) {
    if (rowData.inventoryType == 'SHORT') {
      return Math.abs(Number(rowData.units))
    } else if (rowData.inventoryType == 'LONG') {
      return -1 * Math.abs(Number(rowData.units))
    } else {
      return unit
    }
  }

  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.errorDetails = ''
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: BulkDirectTransferRow[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })

    this.gridApi?.api.setRowData(newRowData)
    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.DIRECT_TRANSFER,
      isDirectSecurityTransfer: true,
      requestMode: bulkRequestMode(this.transInput?.modalMode),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (isBulkEditModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_DIRECT_TRANSFER_SAVE_NOTE_CW_EDIT
      this.bulkTransactionService
        .editBulkTransaction(tranCommand, TranCategoryUrl.DIRECT_TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_DIRECT_TRANSFER_SAVE_NOTE_CW_CREATE
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.DIRECT_TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction create command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    }
  }

  generateEmptyDataEntry(
    activated: boolean,
    id: number,
  ): BulkDirectTransferRow {
    return {
      rowId: id.toString(),
      sourceAccountId: '',
      destAccountId: '',
      entryDate: '',
      assetId: '',
      marketPrice: '',
      units: '',
      inventoryType: activated ? 'LONG' : '',
      mvtBasisSelection: [],
      collateralBroker: '',
      transferSpecificLotId: '',
      mvtBasisSelectionText: activated
        ? TransactionConstants.MIN_FIELD_MESSAGE
        : '',
      validityStatus: activated ? 'intermediate' : 'inactive',
      errorMessageMap: new Map(),
    }
  }

  generateDataEntryFromPositionTransfer(
    positionData: BulkTransferPositionData,
    index: number,
  ): BulkDirectTransferRow {
    return {
      rowId: (index + 1).toString(),
      sourceAccountId: positionData.accountId.toString(),
      entryDate: positionData.sessionReconDate,
      units: positionData.units.toString(),
      assetId: positionData.assetId,
      destAccountId: '',
      marketPrice: '',
      mvtBasisSelection: [],
      mvtBasisSelectionText: '',
      inventoryType: positionData.inventoryType ?? 'LONG',
      collateralBroker: positionData.collateralBroker ?? '',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
    }
  }

  generateDataEntryFromDirectTransfer(
    cwTransaction: BulkDirectTransferTransaction,
    index: number,
  ): BulkDirectTransferRow {
    const mvtBasis = !isBulkCopyModal(this.transInput)
      ? this.mvtMapByTransaction[cwTransaction.legacyOrigTranId ?? 0]
      : ''
    const mvtBasisSelection = !isBulkCopyModal(this.transInput)
      ? buildMvtBasisSelection(
          cwTransaction.accountId,
          mvtBasis as unknown as MvtBasis,
          false,
          cwTransaction.destAccountId,
        )
      : []
    const directTransfer = {
      legacyOrigTranId: cwTransaction.legacyOrigTranId,
      rowId: (index + 1).toString(),
      sourceAccountId: cwTransaction.accountId.toString() ?? '',
      destAccountId: cwTransaction.destAccountId?.toString() ?? '',
      entryDate: cwTransaction.entryDate,
      assetId: cwTransaction.cusip ?? '',
      marketPrice: cwTransaction.transferPrice?.toString() ?? '',
      units: cwTransaction.units.toString() ?? '',
      inventoryType: cwTransaction.inventoryType ?? 'LONG',
      mvtBasisSelection: mvtBasisSelection,
      mvtBasisSelectionText: !isBulkCopyModal(this.transInput)
        ? this.getMarketValueTextFromMvtSelected(mvtBasisSelection)
        : '',
      collateralBroker: cwTransaction.dataMap?.collateralBroker ?? '',
      validityStatus: 'intermediate',
      securityId: cwTransaction.securityId,
      transferSpecificLotId: '',
      errorMessageMap: new Map(),
      transactionBasis: cwTransaction.transactionBasis,
      basisId: cwTransaction.basisId,
    } as BulkDirectTransferRow
    if (cwTransaction.orderId) {
      directTransfer.orderId = cwTransaction.orderId
    }
    return directTransfer
  }

  getMarketValueTextFromMvtSelected(
    mvtBasisSelection: MvtBasisSelection[],
  ): string {
    const marketValueBasis = mvtBasisSelection.filter(
      bases => bases.marketValue,
    )
    return marketValueBasis.length == 0
      ? TransactionConstants.NO_MARKET_VALUE_TRANSFER
      : marketValueBasis.map(bases => bases.basis.prefix).join()
  }

  createFreshRowData() {
    this.rowData = []
    this.rowData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.rowData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  getPricingInfoByPricingRequest(
    accountIdAssetIdEntryDateKey: string,
  ): PricingResponse | undefined {
    return this.pricingInfos.get(accountIdAssetIdEntryDateKey)
  }

  getPopUpRenderer(params: ICellRendererParams) {
    return {
      component: NgGridComponents.modalLinkRenderer,
      params: {
        modalComponent: BvMvBasisComponent,
        modalOptions: {
          size: 'md',
          windowClass: 'modal-window-click-through',
        } as NgbModalOptions,
        beforeOpen: () => this.shouldOpenMvtPicker(params.data),
        modalComplete: (mvtBasisSelectionUpdated: MvtBasisSelection[]) => {
          if (mvtBasisSelectionUpdated.length > 0)
            this.updateBasis(params.node, mvtBasisSelectionUpdated)
        },
      } as ModalLinkRendererParams,
    }
  }

  shouldOpenMvtPicker(data: BulkDirectTransferRow): boolean {
    return (
      data.destAccountId != '' &&
      data.sourceAccountId != '' &&
      data.entryDate != ''
    )
  }

  updateBasis(node: RowNode, mvtBasisSelectionUpdated: MvtBasisSelection[]) {
    node.setDataValue('mvtBasisSelection', mvtBasisSelectionUpdated)
    this.setMarketValueTextFromMvtSelected(mvtBasisSelectionUpdated, node)
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  initializeMvtBasis(rowNode: RowNode, isMarketValueTextNotPasted: boolean) {
    if (
      Number(rowNode.data.destAccountId) == Number(rowNode.data.sourceAccountId)
    ) {
      return
    }
    const mvtData: MarketValueTransferModel = {
      category: TranCategory.DIRECT_TRANSFER,
      accountId: Number(rowNode.data.sourceAccountId),
      tranType: 'TRANSFER_OUT',
      destAccountId: Number(rowNode.data.destAccountId),
      entryDate: rowNode.data.entryDate,
    }
    const mvtDataKey = JSON.stringify(mvtData)

    let mvtBasisPromise: Promise<MvtBasis>

    // Check to see if there is already a Promise for this request
    const mvtBasisExisting = this.mvtDataLoaded.get(mvtDataKey)
    if (mvtBasisExisting) {
      mvtBasisPromise = mvtBasisExisting
    } else {
      // Create a new Promise for this request
      mvtBasisPromise = new Promise<MvtBasis>((resolve, reject) => {
        this.loadingBasis = true
        this.loadingText = TransactionConstants.LOAD_BASIS
        this.bulkTransactionService
          .loadMvtValues(mvtData)
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(
            mvtBasis => {
              this.loadingBasis = false
              resolve(mvtBasis) // Complete Promise with value from Observable
            },
            err => {
              this.errorText = TransactionConstants.ERROR_LOAD_BASIS
              this.errorDetails = err.error
              reject(err)
            },
          )
      })

      // Add new Promise to map, for any future requests
      this.mvtDataLoaded.set(mvtDataKey, mvtBasisPromise)
    }

    // Update row based on new or existing Promise
    mvtBasisPromise.then(mvtBasis => {
      this.setUpMvtBasis(rowNode, isMarketValueTextNotPasted, mvtBasis)
    })
  }

  async loadMvtDetailsBulk(transactions: BulkDirectTransferTransaction[]) {
    const bulkMvtRequests: MarketValueTransferModel[] = []
    transactions.forEach(trans => {
      if (trans.accountId != trans.destAccountId) {
        const mvtRequests = {
          category: 'DIRECT_TRANSFER',
          accountId: trans.accountId,
          tranType: 'TRANSFER_OUT',
          destAccountId: trans.destAccountId,
          entryDate: trans.entryDate,
          tranId: trans.transactionId,
          origTransactionId: trans.legacyOrigTranId,
        } as MarketValueTransferModel
        bulkMvtRequests.push(mvtRequests)
      }
    })
    const bulkMvts$: Observable<BulkMvtResponse> =
      this.bulkTransactionService.loadBulkMvtValues({
        mvtRequests: bulkMvtRequests,
      })
    return Promise.all([bulkMvts$.toPromise()]).then(([bulkMvts]) => {
      this.mvtMapByTransaction = bulkMvts.mvtDetail ?? {}
    })
  }

  setUpMvtBasis(
    rowNode: RowNode,
    isMarketValueTextNotPasted: boolean,
    mvtBasis: MvtBasis,
  ) {
    const allBasis = buildMvtBasisSelection(
      Number(rowNode.data.sourceAccountId),
      mvtBasis,
      false,
      Number(rowNode.data.destAccountId),
    )
    rowNode.setDataValue('mvtBasisSelection', allBasis)
    if (isMarketValueTextNotPasted)
      this.setMarketValueTextFromMvtSelected(allBasis, rowNode)
  }

  setMarketValueTextFromMvtSelected(
    mvtBasisSelection: MvtBasisSelection[],
    rowNode: RowNode,
  ) {
    const marketValueBasis = mvtBasisSelection.filter(
      bases => bases.marketValue,
    )
    rowNode.setDataValue(
      'mvtBasisSelectionText',
      marketValueBasis.length == 0
        ? TransactionConstants.NO_MARKET_VALUE_TRANSFER
        : marketValueBasis.map(bases => bases.basis.prefix).join(),
    )
  }

  handleMvtSelectedChanged(rowNode: RowNode) {
    if (!this.shouldOpenMvtPicker(rowNode.data)) {
      rowNode.setDataValue(
        'mvtBasisSelectionText',
        TransactionConstants.MIN_FIELD_MESSAGE,
      )
      rowNode.setDataValue('mvtBasisSelection', [])
    } else {
      if (
        rowNode.data.mvtBasisSelectionText ==
        TransactionConstants.NO_MARKET_VALUE_TRANSFER
      ) {
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = false
            mvtBasis.bookValue = true
          },
        )
      } else {
        const allMvBasisPrefix: string[] =
          rowNode.data.mvtBasisSelectionText.split(',')
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
            mvtBasis.bookValue = !allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
          },
        )
      }
    }
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }
}
